import { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, MailWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { Toast } from '../../../components/design-system/common';
import { ValidatedInput, AuthLayout, AuthRightPanel, AuthLeftPanel, GoogleContinue } from '../../../components/design-system/auth';
import { useGoogleClientId } from '../../../common/hooks/use-google-client-id.hook';
import { useSignupApi } from '../../../common/hooks/use-signup-api.hook';
import AuthClientStore from '../../../common/services/auth-client.service';
import { readSessionError, takeSignoutNotice } from '../../../common/services/device-session.service';
import { SignedOutNotice } from './components/SignInNotices';
import { useAccountApi } from '../../../common/hooks/use-account-api.hook';
import { SIGNUP_PATH } from '../../../router/account-paths';

const LoginPage = () => {
    const { loginWithoutRedirect, completeAuthentication, setGlobalLoading, me } = useAuthContext();
    const signupApi = useSignupApi();
    const googleClientId = useGoogleClientId();
    // Google said who this is, but there is no account for that address yet.
    const [googleProblem, setGoogleProblem] = useState<'NO_ACCOUNT' | 'FAILED' | 'DISABLED' | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const accountApi = useAccountApi();
    // "Already has an account — sign in" from the signup page brings the email along.
    const location = useLocation();

    const [email, setEmail] = useState(() => (location.state as any)?.email ?? '');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [keepLoggedIn, setKeepLoggedIn] = useState(false);
    const [isBadLogin, setIsBadLogin] = useState(false)
    const [isLoggingIn, setIsLoggingIn] = useState(false)
    const [showValidationError, setShowValidationError] = useState(false)
    // Why this device was just signed out (read once).
    const [signoutNotice] = useState(() => takeSignoutNotice())
    // Right password, but the email was never confirmed (an account from a shared invitation link).
    const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
    const [resent, setResent] = useState(false)

    // Validation helpers
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPasswordValid = password.length >= 4;

    const signIn = async (email, password) => {
        setIsBadLogin(false)
        setUnverifiedEmail(null)
        setResent(false)

        // Validate email and password before proceeding
        if (!isEmailValid || !isPasswordValid) {
            setShowValidationError(true)
            return
        }
        setShowValidationError(false)

        // The button spins while the credentials are checked.
        setIsLoggingIn(true)

        let user
        try {
            user = await loginWithoutRedirect(email, password)
        } catch (error) {
            setIsLoggingIn(false)
            const body = await readSessionError(error)
            if (body?.code === 'EMAIL_NOT_VERIFIED') {
                setUnverifiedEmail((body as any).email || email)
            } else {
                setIsBadLogin(true)
            }
            return
        }

        // The overlay covers the switch to the app and stays until the page
        // it lands on has loaded (AuthProvider clears it) — no fixed waits.
        setGlobalLoading(true, t('AUTH.LOADING.WORKSPACE'))
        completeAuthentication(user)
        navigate('/dashboard')
    };

    /** Google instead of a password: the account linked to it, or the one with that verified address. */
    const signInWithGoogle = async (credential: string) => {
        setGoogleProblem(null);
        setIsBadLogin(false);
        setIsLoggingIn(true);
        try {
            const tokens = await signupApi.googleSignIn(credential);
            AuthClientStore.setAccessToken(tokens.access_token);
            AuthClientStore.setRefreshToken(tokens.refresh_token);
        } catch (error) {
            setIsLoggingIn(false);
            const body: any = await readSessionError(error);
            const status = (error as any)?.status;
            setGoogleProblem(body?.code === 'NO_ACCOUNT' ? 'NO_ACCOUNT' : status === 403 ? 'DISABLED' : 'FAILED');
            return;
        }
        setGlobalLoading(true, t('AUTH.LOADING.WORKSPACE'));
        try {
            await me();
        } catch {
            setGlobalLoading(false);
            setIsLoggingIn(false);
            setGoogleProblem('FAILED');
            return;
        }
        navigate('/dashboard');
    };

    const resendConfirmation = async () => {
        try {
            await accountApi.resendVerification(unverifiedEmail)
        } catch {
            // Same answer either way; a failure here changes nothing for the person.
        }
        setResent(true)
    }

    const leftPanel = (
        // New stores start for free (/start); teammates still come from invitations.
        <AuthLeftPanel
            title={t('AUTH.LOGIN.TITLE')}
            subtitle={t('AUTH.LOGIN.SUBTITLE')}
            fadeClassName="login-panel-fade"
            switchText={t('SIGNUP.LOGIN_LINK.TEXT')}
            switchLinkText={t('SIGNUP.LOGIN_LINK.CTA')}
            switchLinkHref={SIGNUP_PATH}
        >
            {unverifiedEmail ? (
                <Alert className="animate-form-item mb-6" data-testid="email-not-verified">
                    <MailWarning />
                    <AlertTitle>{t('ACCOUNT.LOGIN.NOT_VERIFIED_TITLE')}</AlertTitle>
                    <AlertDescription>
                        <p>{t('ACCOUNT.LOGIN.NOT_VERIFIED', { email: unverifiedEmail })}</p>
                        {resent ? (
                            <p className="mt-2 font-medium" role="status">{t('ACCOUNT.LOGIN.RESENT')}</p>
                        ) : (
                            <Button variant="outline" size="sm" className="mt-2" onClick={resendConfirmation}>
                                {t('ACCOUNT.LOGIN.RESEND')}
                            </Button>
                        )}
                    </AlertDescription>
                </Alert>
            ) : signoutNotice && <SignedOutNotice notice={signoutNotice} />}

            {googleProblem && (
                <Alert className="animate-form-item mb-6" variant={googleProblem === 'NO_ACCOUNT' ? 'default' : 'destructive'} data-testid={`google-signin-${googleProblem.toLowerCase()}`}>
                    <AlertDescription>
                        {googleProblem === 'NO_ACCOUNT' ? (
                            <p>
                                {t('AUTH.LOGIN.GOOGLE_NO_ACCOUNT')}{' '}
                                <Link to={SIGNUP_PATH} className="font-medium text-foreground underline-offset-2 hover:underline">
                                    {t('SIGNUP.LOGIN_LINK.CTA')}
                                </Link>
                            </p>
                        ) : (
                            <p>{t(googleProblem === 'DISABLED' ? 'AUTH.LOGIN.GOOGLE_DISABLED' : 'AUTH.LOGIN.GOOGLE_FAILED')}</p>
                        )}
                    </AlertDescription>
                </Alert>
            )}

            <GoogleContinue
                clientId={googleClientId}
                onCredential={signInWithGoogle}
                onError={() => setGoogleProblem('FAILED')}
                context="signin"
                text="signin_with"
                testId="login-google"
            />

            <form
                noValidate
                aria-busy={isLoggingIn}
                onSubmit={(event) => {
                    event.preventDefault();
                    signIn(email, password);
                }}
            >
                <fieldset disabled={isLoggingIn} className="contents">
                    <ValidatedInput
                        label={t('AUTH.EMAIL')}
                        placeholder="hello@example.com"
                        value={email}
                        onChange={(value) => {
                            setEmail(value);
                            setShowValidationError(false);
                        }}
                        type="email"
                        autoComplete="email"
                        error={showValidationError && !isEmailValid}
                        errorMessage={t('AUTH.ERRORS.EMAIL')}
                        startIcon={<Mail />}
                        className="animate-form-item animate-delay-2"
                    />

                    <ValidatedInput
                        label={t('AUTH.PASSWORD')}
                        placeholder="••••••••"
                        value={password}
                        onChange={(value) => {
                            setPassword(value);
                            setShowValidationError(false);
                        }}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        error={showValidationError && !isPasswordValid}
                        errorMessage={t('AUTH.ERRORS.PASSWORD')}
                        startIcon={<Lock />}
                        endIcon={
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? t('AUTH.HIDE_PASSWORD') : t('AUTH.SHOW_PASSWORD')}
                                className="flex items-center transition-colors hover:text-foreground"
                            >
                                {showPassword ? <EyeOff /> : <Eye />}
                            </button>
                        }
                        className="animate-form-item animate-delay-3 mt-4"
                    />

                    <div className="animate-form-item animate-delay-4 mt-4 mb-6 flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                            <Checkbox checked={keepLoggedIn} onCheckedChange={(v) => setKeepLoggedIn(v === true)} />
                            {t('AUTH.LOGIN.KEEP_LOGGED_IN')}
                        </Label>
                        {/* What was typed goes along in router state, not the URL. */}
                        <Link to="/forgot-password" state={{ email }} className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                            {t('AUTH.LOGIN.FORGOT')}
                        </Link>
                    </div>

                    <Button type="submit" className="animate-form-item animate-delay-5 h-11 w-full">
                        {isLoggingIn ? <Spinner className="size-5" aria-label={t('AUTH.LOGIN.SIGNING_IN')} /> : t('AUTH.LOGIN.SUBMIT')}
                    </Button>
                </fieldset>
            </form>
        </AuthLeftPanel>
    );

    const rightPanel = <AuthRightPanel className="login-panel-fade" />;

    return (
        <>
            <Toast
                message={t('AUTH.ERRORS.BAD_LOGIN')}
                type="error"
                isVisible={isBadLogin}
                onClose={() => setIsBadLogin(false)}
            />

            <AuthLayout leftPanel={leftPanel} rightPanel={rightPanel} />
        </>
    );
};

export default LoginPage;
